import {
    Card,
    DataTable,
    LegalPageShell,
    Section,
    SubHeading,
} from '@/components/LegalPage';

const SECTIONS = [
    { id: 'collect', title: '1. Information We Collect' },
    { id: 'use', title: '2. How We Use Information' },
    { id: 'legal-basis', title: '3. Legal Basis for Processing' },
    { id: 'share', title: '4. How We Share Information' },
    { id: 'team', title: '5. Team and Business Data' },
    { id: 'permissions', title: '6. Device Permissions' },
    { id: 'retention', title: '7. Data Retention' },
    { id: 'security', title: '8. Data Security' },
    { id: 'rights', title: '9. Your Rights and Choices' },
    { id: 'children', title: "10. Children's Privacy" },
    { id: 'transfers', title: '11. International Data Transfers' },
    { id: 'third-party', title: '12. Third-Party Services' },
    { id: 'changes', title: '13. Changes to This Policy' },
    { id: 'contact', title: '14. Contact Us' },
];

function ChancoPrivacyPolicy() {
    return (
        <LegalPageShell
            brand="Chanco"
            pageTitle="Privacy Policy"
            documentTitle="Privacy Policy | Chanco"
            effectiveDate="July 29, 2026"
            lastUpdated="July 29, 2026"
            toc={SECTIONS}
            footerNote="© 2026 Code X Pvt Ltd. All rights reserved. This page is the privacy policy for the Chanco application."
            lead={(
                <>
                    <p>
                        <span className="font-medium text-foreground">Chanco</span>
                        {' '}
                        is a mobile app built by
                        {' '}
                        <span className="font-medium text-foreground">Code X Pvt Ltd</span>
                        {' '}
                        (&quot;Code X&quot;, &quot;we&quot;, or &quot;our&quot;) that helps
                        businesses track inventory, manage orders and quotations, and keep their
                        teams organized in one place. To do that, the app and its backend
                        (together, the &quot;Service&quot;) need to handle some of your
                        information — this policy sets out exactly what we handle, why, and the
                        choices you have.
                    </p>
                    <p>
                        We&apos;ve written it to be specific to how Chanco works, rather than a
                        generic template. Using the app means you&apos;re comfortable with the
                        practices described here; if anything below doesn&apos;t sit right with
                        you, please reach out to us before continuing to use the Service.
                    </p>
                </>
            )}
        >
            <Section id="collect" title="1. Information We Collect">
                <p>
                    We collect the following categories of information to provide and improve
                    the Service.
                </p>

                <SubHeading>Information you provide directly</SubHeading>
                <DataTable
                    rows={[
                        {
                            category: 'Account information',
                            examples: 'Name, email address, phone number, and password when you '
                                + 'register or sign in',
                        },
                        {
                            category: 'Business profile',
                            examples: 'Business name, type, address, city, state, ward, PAN/VAT '
                                + 'number, and location coordinates',
                        },
                        {
                            category: 'Inventory and operational data',
                            examples: 'Products, categories, stock levels, orders, quotations, '
                                + 'customers, pricing, and reports',
                        },
                        {
                            category: 'Images',
                            examples: 'Photos uploaded from your camera or photo library, e.g. '
                                + 'product or profile images',
                        },
                        {
                            category: 'Support communications',
                            examples: 'Information you provide when you contact us',
                        },
                    ]}
                />

                <SubHeading>Information collected automatically</SubHeading>
                <ul className="list-disc space-y-1.5 pl-5">
                    <li>
                        <span className="font-medium text-foreground">Location data</span>
                        {' '}
                        — with your permission, we access your device&apos;s approximate and/or
                        precise location to show your business on the map and provide
                        location-based features. You can revoke this at any time in your device
                        settings.
                    </li>
                    <li>
                        <span className="font-medium text-foreground">Device information</span>
                        {' '}
                        — device model, operating system version, unique device identifiers, and
                        app version, used for security, notifications, and troubleshooting
                    </li>
                    <li>
                        <span className="font-medium text-foreground">Usage and diagnostic data</span>
                        {' '}
                        — how you interact with the Service, including feature usage and error
                        logs
                    </li>
                    <li>
                        <span className="font-medium text-foreground">Push notification tokens</span>
                        {' '}
                        — a token that lets us deliver push notifications to your device
                    </li>
                </ul>

                <SubHeading>Information stored on your device</SubHeading>
                <ul className="list-disc space-y-1.5 pl-5">
                    <li>
                        Authentication tokens are stored securely on your device to keep you
                        signed in
                    </li>
                    <li>
                        App preferences and cached data may be stored locally to improve
                        performance
                    </li>
                </ul>
            </Section>

            <Section id="use" title="2. How We Use Information">
                <ul className="list-disc space-y-1.5 pl-5">
                    <li>Create and manage your account and authenticate you</li>
                    <li>
                        Provide, operate, and maintain the Service&apos;s features (inventory,
                        orders, quotations, reports, mapping, etc.)
                    </li>
                    <li>Send push notifications and service-related communications</li>
                    <li>Personalize and improve the Service and develop new features</li>
                    <li>Monitor usage, diagnose problems, and maintain security and integrity</li>
                    <li>Comply with legal obligations and enforce our terms</li>
                </ul>
                <p>
                    We do
                    {' '}
                    <span className="font-medium text-foreground">not</span>
                    {' '}
                    sell your personal information.
                </p>
            </Section>

            <Section id="legal-basis" title="3. Legal Basis for Processing">
                <p>
                    Where applicable law requires a legal basis for processing your information,
                    we rely on one or more of the following:
                </p>
                <ul className="list-disc space-y-1.5 pl-5">
                    <li>
                        <span className="font-medium text-foreground">Contract</span>
                        {' '}
                        — to provide the Service you have signed up for
                    </li>
                    <li>
                        <span className="font-medium text-foreground">Consent</span>
                        {' '}
                        — for optional permissions such as location, camera, and notifications,
                        which you can withdraw at any time
                    </li>
                    <li>
                        <span className="font-medium text-foreground">Legitimate interests</span>
                        {' '}
                        — to secure, maintain, and improve the Service
                    </li>
                    <li>
                        <span className="font-medium text-foreground">Legal obligation</span>
                        {' '}
                        — to comply with applicable laws and regulations
                    </li>
                </ul>
            </Section>

            <Section id="share" title="4. How We Share Information">
                <p>We share information only as needed to run the Service:</p>
                <ul className="list-disc space-y-1.5 pl-5">
                    <li>
                        <span className="font-medium text-foreground">Service providers</span>
                        {' '}
                        — third parties that host our infrastructure, deliver push notifications,
                        provide app updates, and support diagnostics, acting on our behalf and
                        under confidentiality obligations. These include Expo (app updates and
                        notification delivery) and Google (Android services and notification
                        delivery).
                    </li>
                    <li>
                        <span className="font-medium text-foreground">Within your business</span>
                        {' '}
                        — data you create may be visible to other members of your business
                        account according to their assigned roles and permissions
                    </li>
                    <li>
                        <span className="font-medium text-foreground">Legal and safety</span>
                        {' '}
                        — when required by law, regulation, or legal process, or to protect the
                        rights, property, or safety of Code X, our users, or others
                    </li>
                    <li>
                        <span className="font-medium text-foreground">Business transfers</span>
                        {' '}
                        — in connection with a merger, acquisition, or sale of assets, subject to
                        this policy
                    </li>
                </ul>
            </Section>

            <Section id="team" title="5. Team and Business Data">
                <p>
                    Chanco supports multiple team members within a single business account, each
                    with a role and permissions set by the business owner. Business owners and
                    administrators can invite, manage, and remove team members, and may have
                    access to activity and data created by members of their business.
                </p>
                <p>
                    If you join a business account created by someone else, the owner of that
                    business acts as the controller of the business data, and their handling of
                    that data is subject to their own arrangements with you.
                </p>
            </Section>

            <Section id="permissions" title="6. Device Permissions">
                <p>
                    The Service may request the following permissions. You can grant or deny
                    each one, and change them later in your device settings.
                </p>
                <DataTable
                    rows={[
                        { category: 'Location (coarse & fine)', examples: 'Show your business on the map and provide location-based features' },
                        { category: 'Camera / Photos', examples: 'Capture or upload product and profile images' },
                        { category: 'Notifications', examples: 'Deliver order updates, alerts, and other push notifications' },
                        { category: 'Vibrate', examples: 'Provide haptic feedback with notifications' },
                    ]}
                />
                <p>
                    Denying a permission may limit certain features but will not prevent you
                    from using the core Service.
                </p>
            </Section>

            <Section id="retention" title="7. Data Retention">
                <p>
                    We retain your personal information for as long as your account is active or
                    as needed to provide the Service, comply with our legal obligations, resolve
                    disputes, and enforce our agreements. When information is no longer needed, we
                    delete or anonymize it.
                </p>
            </Section>

            <Section id="security" title="8. Data Security">
                <p>
                    We use reasonable technical and organizational measures to protect your
                    information, including encrypted transmission and secure on-device storage of
                    authentication tokens. However, no method of transmission or storage is
                    completely secure, and we cannot guarantee absolute security.
                </p>
            </Section>

            <Section id="rights" title="9. Your Rights and Choices">
                <p>Depending on your location, you may have the right to:</p>
                <ul className="list-disc space-y-1.5 pl-5">
                    <li>Access, correct, or update your personal information</li>
                    <li>Request deletion of your account and associated data</li>
                    <li>Object to or restrict certain processing</li>
                    <li>Withdraw consent (for example, by disabling permissions)</li>
                    <li>Request a copy of your data (data portability)</li>
                </ul>
                <p>
                    To exercise any of these rights, contact us at
                    {' '}
                    <a href="mailto:codexpokhara@gmail.com" className="text-primary underline-offset-4 hover:underline">
                        codexpokhara@gmail.com
                    </a>
                    . You can also manage permissions and notifications directly in your device
                    settings.
                </p>
            </Section>

            <Section id="children" title="10. Children's Privacy">
                <p>
                    The Service is not directed to children under the age of 13 (or the minimum
                    age required in your jurisdiction). We do not knowingly collect personal
                    information from children. If you believe a child has provided us with
                    personal information, please contact us and we will delete it.
                </p>
            </Section>

            <Section id="transfers" title="11. International Data Transfers">
                <p>
                    Your information may be processed and stored in countries other than your
                    own. Where required, we take steps to ensure your information receives an
                    adequate level of protection in accordance with applicable law.
                </p>
            </Section>

            <Section id="third-party" title="12. Third-Party Services">
                <Card>
                    <p className="text-[0.95rem] leading-relaxed text-muted-foreground">
                        The Service relies on third-party providers that have their own privacy
                        policies, including:
                    </p>
                    <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[0.95rem] leading-relaxed text-muted-foreground">
                        <li>
                            <span className="font-medium text-foreground">Expo / Expo Application Services</span>
                            {' — '}
                            <a href="https://expo.dev/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-4 hover:underline">
                                expo.dev/privacy
                            </a>
                        </li>
                        <li>
                            <span className="font-medium text-foreground">Google Play Services & Firebase</span>
                            {' — '}
                            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-4 hover:underline">
                                policies.google.com/privacy
                            </a>
                        </li>
                        <li>
                            <span className="font-medium text-foreground">Apple App Store services</span>
                            {' — '}
                            <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-4 hover:underline">
                                apple.com/legal/privacy
                            </a>
                        </li>
                    </ul>
                    <p className="mt-3 text-[0.95rem] leading-relaxed text-muted-foreground">
                        We are not responsible for the privacy practices of these third parties.
                    </p>
                </Card>
            </Section>

            <Section id="changes" title="13. Changes to This Policy">
                <p>
                    We may update this Privacy Policy from time to time. When we do, we will
                    revise the &quot;Last updated&quot; date above and, where appropriate, notify
                    you within the Service. Your continued use of Chanco after changes take
                    effect constitutes acceptance of the updated policy.
                </p>
            </Section>

            <Section id="contact" title="14. Contact Us">
                <p>
                    If you have questions or concerns about this Privacy Policy or our data
                    practices, contact us at:
                </p>
                <Card className="text-[0.95rem] text-muted-foreground">
                    <p className="font-medium text-foreground">Code X Pvt Ltd</p>
                    <p className="mt-1">
                        Email:
                        {' '}
                        <a href="mailto:codexpokhara@gmail.com" className="text-primary underline-offset-4 hover:underline">
                            codexpokhara@gmail.com
                        </a>
                    </p>
                </Card>
            </Section>
        </LegalPageShell>
    );
}

export default ChancoPrivacyPolicy;
